import { Component, ElementRef, EventEmitter, Input, OnChanges, Output, QueryList, SimpleChanges, ViewChild, ViewChildren } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatDialog } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltip, MatTooltipModule } from '@angular/material/tooltip';
import { CdkDropList, CdkDrag, CdkDragDrop, CdkDragEnd, CdkDragPlaceholder, CdkDragPreview, CdkDragStart } from '@angular/cdk/drag-drop';
import { NgxCurrencyDirective } from 'ngx-currency';
import { MaskService } from '@shared/services/mask.service';
import { VariavelStruct } from '@shared/services/structs/formula/variavel.struct';
import { FormulaExpressaoToken, FormulaExpressaoValidacao, tokenizarExpressaoFormula, validarExpressaoFormula } from '../utils/formula-expressao.util';
import { classeBadgeFuncao, classeBadgeTokenFormula, classeBadgeVariavel, isVariavelParametro } from '../utils/formula-badge.util';
import { FormulaTokenTipoEnum } from '@shared/enums/formula/formula-token-tipo.enum';
import { ExpressionToken } from './expression-token';
import { FormulaFuncaoBloco, mapearBlocosFuncaoMarcados, marcarBlocosFuncao, obterBlocoFuncaoEstrutural } from '../utils/formula-funcao-bloco';
import { TipoConstanteNumericaEnum } from '@shared/enums/motor-fiscal/tipo-constante-numerica.enum';
import { TipoDadoVariavelEnum } from '@shared/enums/formula/tipo-dado-variavel.enum';
import { UtilService } from '@shared/services/util.service';
import { ConstanteNumericaConversaoDialogComponent } from './constante-numerica-conversao-dialog.component';
import { TipoArredondamentoEnum } from '@shared/enums/formula/tipo-arredondamento.enum';
import { ConfirmModalComponent } from '@shared/components/confirm-modal/confirm-modal.component';
import { AlertService, AlertType } from '@shared/services/alert.service';

export { ExpressionToken } from './expression-token';

/**
 * Editor de expressão de fórmula (equation editor) baseado em Angular CDK DragDrop.
 *
 * A árvore de tokens (lista PLANA de badges) é a FONTE DA VERDADE: operações mutam o
 * array no lugar e só re-parseiam a string de entrada quando ela muda por fora — nunca
 * em resposta ao próprio emit. Isso mantém cursor/seleção estáveis e o drag/drop sólido.
 *
 * Os blocos de função são lógicos: a expressão permanece plana para persistência. Apenas
 * os tokens estruturais selecionam a estrutura e iniciam o arraste da função completa;
 * argumentos internos continuam selecionáveis e movimentáveis de forma independente.
 */
@Component({
    selector: 'app-formula-expressao-editor',
    standalone: true,
    imports: [
        CommonModule,
        ReactiveFormsModule,
        MatButtonModule,
        MatButtonToggleModule,
        MatIconModule,
        MatTooltipModule,
        CdkDropList,
        CdkDrag,
        CdkDragPlaceholder,
        CdkDragPreview,
        NgxCurrencyDirective,
    ],
    templateUrl: './formula-expressao-editor.component.html',
    styleUrl: './formula-expressao-editor.component.css',
})
export class FormulaExpressaoEditorComponent implements OnChanges {
    private static readonly VALOR_MAXIMO_INTEIRO = 999_999_999;
    private static readonly VALOR_MAXIMO_DECIMAL = 999_999_999.99;
    private static readonly DIGITOS_MAXIMOS_INTEIRO = 9;
    private static readonly DIGITOS_MAXIMOS_DECIMAL = 11;

    public readonly FormulaTokenTipoEnum = FormulaTokenTipoEnum;
    public readonly TipoConstanteNumericaEnum = TipoConstanteNumericaEnum;
    @Input({ required: true }) expressao!: string;
    @Output() expressaoChange = new EventEmitter<string>();
    @Input() variaveis: VariavelStruct[] = [];

    @ViewChild('editorRoot') private editorRoot?: ElementRef<HTMLElement>;
    @ViewChildren('constInput') private constInputs!: QueryList<ElementRef<HTMLInputElement>>;
    @ViewChildren('tokenElement') private tokenElements!: QueryList<ElementRef<HTMLElement>>;

    /** Lista plana de tokens (fonte da verdade). */
    public tokens: ExpressionToken[] = [];

    /** Cursor: índice de inserção [0..tokens.length]. */
    public cursorIndex = 0;

    /** Seleção (índices planos). */
    public selectedSet = new Set<number>();

    /** Token numérico em edição inline (constante). */
    public editing: ExpressionToken | null = null;
    private editingTipo = TipoConstanteNumericaEnum.Decimal;
    private limiteConstanteNotificado = false;
    public readonly editControl = new FormControl<number | null>(null);
    public readonly decimalMask2 = this.maskService.getDecimalMask2(FormulaExpressaoEditorComponent.VALOR_MAXIMO_DECIMAL);
    public readonly integerMask = this.maskService.getIntegerMask(FormulaExpressaoEditorComponent.VALOR_MAXIMO_INTEIRO);

    public get mascaraConstanteEmEdicao() {
        return this.editingTipo === TipoConstanteNumericaEnum.Inteiro
            ? this.integerMask
            : this.decimalMask2;
    }

    public get limiteDigitosConstante(): number {
        return this.editingTipo === TipoConstanteNumericaEnum.Inteiro
            ? FormulaExpressaoEditorComponent.DIGITOS_MAXIMOS_INTEIRO
            : FormulaExpressaoEditorComponent.DIGITOS_MAXIMOS_DECIMAL;
    }

    /** Blocos de função reconstruídos sobre a expressão plana. */
    private funcaoBlocos: FormulaFuncaoBloco[] = [];
    private draggedBlock: FormulaFuncaoBloco | null = null;
    private draggedTokenIndex: number | null = null;
    private proximoFuncaoGrupo = 0;
    public activeTokenIndex: number | null = null;

    /** Última string emitida — usado para ignorar o round-trip do próprio emit. */
    private lastEmitted: string | null = null;

    constructor(
        private maskService: MaskService,
        private dialog: MatDialog,
        private alertService: AlertService,
    ) {}

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['expressao'] && (this.expressao ?? '') !== this.lastEmitted) {
            this.tokens = this.parseExpressao(this.expressao ?? '');
            this.cursorIndex = this.tokens.length;
            this.selectedSet = new Set();
            this.activeTokenIndex = null;
            this.editing = null;
            this.recomputeBlocos();
        }
    }

    // ─────────────────────────── Parse / serialização ───────────────────────────

    private parseExpressao(expr: string): ExpressionToken[] {
        const out: ExpressionToken[] = [];
        if (!expr?.trim()) return out;

        for (const token of tokenizarExpressaoFormula(expr)) {
            out.push(token.tipo === FormulaTokenTipoEnum.Operador ? { ...token, valor: ` ${token.valor} ` } : { ...token });
        }
        marcarBlocosFuncao(out, () => this.criarFuncaoGrupoId());
        return out;
    }

    private serialize(): string {
        return this.tokens.map(t => t.valor).join('');
    }

    private emit(): void {
        this.recomputeBlocos();
        const str = this.serialize();
        this.lastEmitted = str;
        this.expressaoChange.emit(str);
    }

    // ─────────────────────────── Estrutura lógica das funções ───────────────────────────

    private recomputeBlocos(): void {
        this.funcaoBlocos = mapearBlocosFuncaoMarcados(this.tokens);
    }

    private blocoEstruturalDoToken(i: number): FormulaFuncaoBloco | null {
        return obterBlocoFuncaoEstrutural(this.funcaoBlocos, i);
    }

    private indicesEstruturais(bloco: FormulaFuncaoBloco): number[] {
        return [...bloco.skeleton].sort((a, b) => a - b);
    }

    private indicesSelecaoToken(i: number): number[] {
        const bloco = this.blocoEstruturalDoToken(i);
        return bloco ? this.indicesEstruturais(bloco) : [i];
    }

    // ─────────────────────────── Inserção ───────────────────────────

    private insereCore(novos: ExpressionToken[]): number {
        const gruposPai = this.gruposNoPontoInsercao(this.cursorIndex, this.funcaoBlocos);
        novos.forEach(token => {
            if (!token.funcaoGrupoIds) token.funcaoGrupoIds = [...gruposPai];
        });
        this.tokens.splice(this.cursorIndex, 0, ...novos);
        const start = this.cursorIndex;
        this.cursorIndex += novos.length;
        return start;
    }

    public inserirToken(token: ExpressionToken): void {
        this.commitEditIfAny();
        this.cursorIndex = this.indiceInsercaoSeguro(this.cursorIndex);
        const start = this.insereCore([{ ...token }]);
        this.selectedSet = new Set();
        this.activeTokenIndex = start;
        this.normalizarOperadoresCompostos();
        this.emit();
    }

    public inserirVariavel(v: VariavelStruct): void {
        const chave = this.variavelChave(v);
        this.inserirToken({ tipo: FormulaTokenTipoEnum.Variavel, valor: `{${chave}}`, display: `{${chave}}` });
    }

    /** Insere a estrutura @se(,,) como tokens planos e posiciona o cursor após o '('. */
    public inserirBlocoSe(): void {
        this.inserirFuncao('@se', 2);
    }

    /** Insere uma soma com um argumento inicial vazio. Outros argumentos podem ser adicionados por vírgula. */
    public inserirBlocoSoma(): void {
        this.inserirFuncao('@soma', 0);
    }

    /** Insere o rateio com valor documental e base por item. */
    public inserirBlocoRateio(): void {
        this.inserirFuncao('@rateio', 1);
    }

    private inserirFuncao(nome: string, separadores: number): void {
        this.commitEditIfAny();
        this.cursorIndex = this.indiceInsercaoSeguro(this.cursorIndex);
        const gruposPai = this.gruposNoPontoInsercao(this.cursorIndex, this.funcaoBlocos);
        const grupoId = this.criarFuncaoGrupoId();
        const grupos = [...gruposPai, grupoId];
        const estrutural = (tipo: FormulaTokenTipoEnum, valor: string): ExpressionToken => ({
            tipo,
            valor,
            display: valor,
            funcaoGrupoIds: [...grupos],
            funcaoEstruturalId: grupoId,
        });
        const novos: ExpressionToken[] = [
            estrutural(FormulaTokenTipoEnum.Funcao, nome),
            estrutural(FormulaTokenTipoEnum.Parentese, '('),
            ...Array.from({ length: separadores }, () => estrutural(FormulaTokenTipoEnum.Separador, ',')),
            estrutural(FormulaTokenTipoEnum.Parentese, ')'),
        ];
        const start = this.insereCore(novos);
        this.cursorIndex = start + 2; // logo após o '('
        this.selectedSet = new Set();
        this.activeTokenIndex = start;
        this.emit();
    }

    /** Insere uma constante numérica vazia já em modo de edição (commit zera se vazio). */
    public inserirConstanteEditavel(): void {
        this.commitEditIfAny();
        this.cursorIndex = this.indiceInsercaoSeguro(this.cursorIndex);
        const token: ExpressionToken = { tipo: FormulaTokenTipoEnum.Constante, valor: '', display: '' };
        this.insereCore([token]);
        // A edição começa antes do primeiro emit; o mapa precisa refletir os novos
        // índices imediatamente para não herdar o contexto estrutural anterior.
        this.recomputeBlocos();
        this.editingTipo = TipoConstanteNumericaEnum.Decimal;
        this.startEdit(token); // emit acontece no commit
    }

    // ─────────────────────────── Cursor / seleção / cliques ───────────────────────────

    public onBackgroundClick(event: MouseEvent): void {
        event.stopPropagation();
        this.commitEditIfAny();
        this.cursorIndex = this.indiceInsercaoSeguro(this.indiceInsercaoNoClique(event));
        this.selectedSet = new Set();
        this.activeTokenIndex = null;
        this.editorRoot?.nativeElement.focus();
    }

    /** Traduz a posição visual do clique no fluxo flexível para um índice da lista plana. */
    private indiceInsercaoNoClique(event: MouseEvent): number {
        const elementos = this.tokenElements?.toArray() ?? [];
        if (elementos.length === 0) return 0;

        const tokensRenderizados = elementos
            .map((elemento, index) => ({ index, rect: elemento.nativeElement.getBoundingClientRect() }))
            .filter(item => item.rect.width > 0 && item.rect.height > 0);
        if (tokensRenderizados.length === 0) return this.tokens.length;

        const linhas: Array<{
            top: number;
            bottom: number;
            tokens: typeof tokensRenderizados;
        }> = [];

        for (const item of tokensRenderizados) {
            const linha = linhas.find(atual => item.rect.top < atual.bottom && item.rect.bottom > atual.top);
            if (linha) {
                linha.top = Math.min(linha.top, item.rect.top);
                linha.bottom = Math.max(linha.bottom, item.rect.bottom);
                linha.tokens.push(item);
                continue;
            }

            linhas.push({ top: item.rect.top, bottom: item.rect.bottom, tokens: [item] });
        }

        linhas.sort((a, b) => a.top - b.top);
        if (event.clientY < linhas[0].top) return 0;
        if (event.clientY > linhas[linhas.length - 1].bottom) return this.tokens.length;

        const linha = linhas.reduce((maisProxima, atual) => {
            const distanciaAtual = event.clientY < atual.top
                ? atual.top - event.clientY
                : event.clientY > atual.bottom ? event.clientY - atual.bottom : 0;
            const distanciaMaisProxima = event.clientY < maisProxima.top
                ? maisProxima.top - event.clientY
                : event.clientY > maisProxima.bottom ? event.clientY - maisProxima.bottom : 0;
            return distanciaAtual < distanciaMaisProxima ? atual : maisProxima;
        });

        const tokensDaLinha = [...linha.tokens].sort((a, b) => a.rect.left - b.rect.left);
        for (const item of tokensDaLinha) {
            const centro = item.rect.left + item.rect.width / 2;
            if (event.clientX < centro) return item.index;
        }

        return tokensDaLinha[tokensDaLinha.length - 1].index + 1;
    }

    public onTokenClick(i: number, event: MouseEvent): void {
        event.stopPropagation();
        this.commitEditIfAny();
        this.activeTokenIndex = i;

        const base = (event.ctrlKey || event.metaKey) ? new Set(this.selectedSet) : new Set<number>();
        const indices = this.indicesSelecaoToken(i);
        const blocoJaSelecionado = indices.every(index => base.has(index));
        if (blocoJaSelecionado) indices.forEach(index => base.delete(index));
        else indices.forEach(index => base.add(index));

        this.selectedSet = this.normalizarSelecao(base);
        this.cursorIndex = this.indiceInsercaoSeguro(i + 1);
    }

    public onTokenDblClick(token: ExpressionToken, event: MouseEvent): void {
        event.stopPropagation();
        if (token.tipo === FormulaTokenTipoEnum.Constante) this.startEdit(token);
    }

    /** Expande somente tokens estruturais para os demais elementos estruturais da função. */
    private normalizarSelecao(base: Set<number>): Set<number> {
        const result = new Set<number>();
        for (const i of base) {
            this.indicesSelecaoToken(i).forEach(index => result.add(index));
        }
        return result;
    }

    public isSelected(i: number): boolean {
        return this.selectedSet.has(i);
    }

    public onKeyDown(event: KeyboardEvent): void {
        if (this.editing) return;

        if (!event.ctrlKey && !event.metaKey && !event.altKey && /^\d$/.test(event.key)) {
            event.preventDefault();
            this.inserirConstanteEditavel();
            this.editControl.setValue(Number(event.key), { emitEvent: false });
            return;
        }

        const teclasOperador: Record<string, ExpressionToken> = {
            '+': { tipo: FormulaTokenTipoEnum.Operador, valor: ' + ', display: '+' },
            '-': { tipo: FormulaTokenTipoEnum.Operador, valor: ' - ', display: '-' },
            '*': { tipo: FormulaTokenTipoEnum.Operador, valor: ' * ', display: 'x' },
            '/': { tipo: FormulaTokenTipoEnum.Operador, valor: ' / ', display: '/' },
            '>': { tipo: FormulaTokenTipoEnum.Operador, valor: ' > ', display: '>' },
            '<': { tipo: FormulaTokenTipoEnum.Operador, valor: ' < ', display: '<' },
            '=': { tipo: FormulaTokenTipoEnum.Operador, valor: ' = ', display: '=' },
            '(': { tipo: FormulaTokenTipoEnum.Parentese, valor: '(', display: '(' },
            ')': { tipo: FormulaTokenTipoEnum.Parentese, valor: ')', display: ')' },
        };
        const tokenDigitado = teclasOperador[event.key];
        if (tokenDigitado) {
            event.preventDefault();
            this.inserirToken(tokenDigitado);
            return;
        }

        if (event.key === 'Delete' || event.key === 'Backspace') {
            event.preventDefault();

            if (this.selectedSet.size === 0) {
                const tokenIndex = event.key === 'Backspace' ? this.cursorIndex - 1 : this.cursorIndex;
                if (tokenIndex < 0 || tokenIndex >= this.tokens.length) return;
                this.selectedSet = this.normalizarSelecao(new Set([tokenIndex]));
            }

            this.removerSelecionados();
            return;
        }

        if (event.key === 'ArrowLeft') {
            event.preventDefault();
            this.cursorIndex = this.indiceInsercaoSeguro(
                Math.max(0, this.cursorIndex - 1),
                -1,
            );
            this.selectedSet = new Set();
            this.activeTokenIndex = null;
            return;
        }

        if (event.key === 'ArrowRight') {
            event.preventDefault();
            this.cursorIndex = this.indiceInsercaoSeguro(
                Math.min(this.tokens.length, this.cursorIndex + 1),
            );
            this.selectedSet = new Set();
            this.activeTokenIndex = null;
            return;
        }
    }

    public removerSelecionados(): void {
        if (this.selectedSet.size === 0) return;
        const indicesRemocao = new Set<number>();
        for (const index of this.selectedSet) {
            const bloco = this.blocoEstruturalDoToken(index);
            if (bloco) {
                for (let i = bloco.start; i <= bloco.end; i++) indicesRemocao.add(i);
            } else {
                indicesRemocao.add(index);
            }
        }
        const primeiroIndex = Math.min(...indicesRemocao);
        [...indicesRemocao].sort((a, b) => b - a).forEach(i => this.tokens.splice(i, 1));
        this.cursorIndex = Math.min(primeiroIndex, this.tokens.length);
        this.selectedSet = new Set();
        this.activeTokenIndex = null;
        this.emit();
    }

    /** Insere um par de agrupamento e mantém o cursor entre os parênteses. */
    public inserirParentesesAgrupamento(): void {
        this.commitEditIfAny();
        this.cursorIndex = this.indiceInsercaoSeguro(this.cursorIndex);
        const start = this.insereCore([
            { tipo: FormulaTokenTipoEnum.Parentese, valor: '(', display: '(' },
            { tipo: FormulaTokenTipoEnum.Parentese, valor: ')', display: ')' },
        ]);
        this.cursorIndex = start + 1;
        this.selectedSet = new Set();
        this.activeTokenIndex = null;
        this.emit();
    }

    /** Remove apenas a constante ativa, mesmo quando ela pertence a um bloco de função selecionado. */
    public removerConstanteAtiva(): void {
        const index = this.activeTokenIndex;
        if (index === null || this.tokens[index]?.tipo !== FormulaTokenTipoEnum.Constante) return;
        this.tokens.splice(index, 1);
        this.cursorIndex = Math.min(index, this.tokens.length);
        this.selectedSet = new Set();
        this.activeTokenIndex = null;
        this.editing = null;
        this.emit();
    }

    public limpar(): void {
        this.tokens.length = 0;
        this.cursorIndex = 0;
        this.selectedSet = new Set();
        this.activeTokenIndex = null;
        this.editing = null;
        this.emit();
    }

    public onDragStarted(index: number, _event: CdkDragStart): void {
        this.activeTokenIndex = index;
        this.draggedTokenIndex = index;
        this.draggedBlock = this.blocoEstruturalDoToken(index);
        this.selectedSet = this.normalizarSelecao(new Set([index]));
    }

    public onDragEnded(_event: CdkDragEnd): void {
        // O CDK pode emitir `ended` antes de `dropped`; adiar evita limpar o bloco
        // antes de o drop consumir o estado estrutural do arraste.
        setTimeout(() => {
            if (this.draggedTokenIndex !== null) this.finalizarArraste();
        });
    }

    /** Os demais tokens do bloco saem do fluxo enquanto o badge da função acompanha o cursor. */
    public isTokenOcultoDuranteArraste(index: number): boolean {
        return this.draggedBlock !== null
            && index !== this.draggedTokenIndex
            && index >= this.draggedBlock.start
            && index <= this.draggedBlock.end;
    }

    public get arrastandoBloco(): boolean {
        return this.draggedBlock !== null;
    }

    public get arrastandoToken(): boolean {
        return this.draggedTokenIndex !== null;
    }

    /** Só permite posições que preservem cada função como bloco estrutural indivisível. */
    public readonly podeOrdenarToken = (index: number): boolean => this.podeSoltarNoIndice(index);

    public podeSoltarNoIndice(index: number): boolean {
        if (this.draggedTokenIndex === null) {
            return !this.funcaoBlocos.some(bloco => index === bloco.start + 1);
        }

        const simulacao = this.simularDrop(index);
        return simulacao !== null && this.estruturaFuncoesPreservada(simulacao.tokens);
    }

    public onDrop(event: CdkDragDrop<ExpressionToken[]>): void {
        const simulacao = this.simularDrop(event.currentIndex);
        if (!simulacao || !this.estruturaFuncoesPreservada(simulacao.tokens)) {
            this.finalizarArraste();
            return;
        }

        this.tokens.splice(0, this.tokens.length, ...simulacao.tokens);
        this.cursorIndex = simulacao.cursorIndex;
        this.normalizarOperadoresCompostos();
        this.finalizarArraste();
        this.emit();
    }

    private simularDrop(currentIndex: number): { tokens: ExpressionToken[]; cursorIndex: number } | null {
        if (this.draggedTokenIndex === null) return null;

        const tokens = [...this.tokens];
        if (!this.draggedBlock) {
            const [token] = tokens.splice(this.draggedTokenIndex, 1);
            const destino = Math.max(0, Math.min(currentIndex, tokens.length));
            const blocosRestantes = mapearBlocosFuncaoMarcados(tokens);
            if (this.pontoInsercaoProtegido(destino, blocosRestantes)) return null;
            token.funcaoGrupoIds = this.gruposNoPontoInsercao(destino, blocosRestantes);
            token.funcaoEstruturalId = undefined;
            tokens.splice(destino, 0, token);
            return { tokens, cursorIndex: Math.min(destino + 1, tokens.length) };
        }

        const { start, end } = this.draggedBlock;

        // Enquanto o bloco está fora do fluxo, o CDK ainda pode informar um índice
        // originalmente ocupado por ele. Essa faixa representa o retorno à origem,
        // não uma tentativa de inserir um token dentro da própria função.
        const indiceDestino = currentIndex >= start && currentIndex <= end
            ? start
            : currentIndex;

        const bloco = tokens.splice(start, end - start + 1);
        const gruposMovidos = new Set(bloco.flatMap(token => {
            const grupos = token.funcaoGrupoIds ?? [];
            const inicio = grupos.indexOf(this.draggedBlock!.id);
            return inicio >= 0 ? grupos.slice(inicio) : [];
        }));
        bloco.forEach(token => token.funcaoGrupoIds = (token.funcaoGrupoIds ?? [])
            .filter(id => gruposMovidos.has(id)));
        const destinoCalculado = indiceDestino > end
            ? indiceDestino - bloco.length + 1
            : indiceDestino;
        const destino = Math.max(0, Math.min(destinoCalculado, tokens.length));
        const blocosRestantes = mapearBlocosFuncaoMarcados(tokens);
        if (this.pontoInsercaoProtegido(destino, blocosRestantes)) return null;
        const gruposPai = this.gruposNoPontoInsercao(destino, blocosRestantes);
        bloco.forEach(token => token.funcaoGrupoIds = [...gruposPai, ...(token.funcaoGrupoIds ?? [])]);
        tokens.splice(destino, 0, ...bloco);
        return { tokens, cursorIndex: Math.min(destino + bloco.length, tokens.length) };
    }

    private estruturaFuncoesPreservada(tokens: ExpressionToken[]): boolean {
        const blocos = mapearBlocosFuncaoMarcados(tokens);
        const ids = new Set(tokens.flatMap(token => token.funcaoGrupoIds ?? []));

        return blocos.length === ids.size && blocos.every(bloco => {
            const indices = tokens
                .map((token, index) => token.funcaoGrupoIds?.includes(bloco.id) ? index : -1)
                .filter(index => index >= 0);
            const contiguo = indices.every((index, posicao) => index === indices[0] + posicao);
            const estruturais = [...bloco.skeleton].sort((a, b) => a - b);
            return contiguo
                && estruturais[0] === bloco.start
                && tokens[bloco.start].tipo === FormulaTokenTipoEnum.Funcao
                && tokens[bloco.start + 1]?.tipo === FormulaTokenTipoEnum.Parentese
                && tokens[bloco.start + 1].valor === '('
                && estruturais.includes(bloco.start + 1)
                && tokens[bloco.end]?.tipo === FormulaTokenTipoEnum.Parentese
                && tokens[bloco.end].valor === ')'
                && estruturais.includes(bloco.end);
        });
    }

    private finalizarArraste(): void {
        this.draggedBlock = null;
        this.draggedTokenIndex = null;
        this.selectedSet = new Set();
        this.activeTokenIndex = null;
    }

    private criarFuncaoGrupoId(): string {
        return `funcao-editor-${++this.proximoFuncaoGrupo}`;
    }

    private gruposNoPontoInsercao(index: number, blocos: FormulaFuncaoBloco[]): string[] {
        return blocos
            .filter(bloco => index > bloco.start && index <= bloco.end)
            .sort((a, b) => a.nivel - b.nivel)
            .map(bloco => bloco.id);
    }

    private pontoInsercaoProtegido(index: number, blocos: FormulaFuncaoBloco[]): boolean {
        return blocos.some(bloco => index === bloco.start + 1);
    }

    private indiceInsercaoSeguro(index: number, direcao: -1 | 1 = 1): number {
        const bloco = this.funcaoBlocos.find(item => index === item.start + 1);
        if (!bloco) return index;
        return direcao < 0 ? bloco.start : bloco.start + 2;
    }

    public blocoPreviewArraste(index: number): FormulaFuncaoBloco | null {
        return this.blocoEstruturalDoToken(index);
    }

    public get blocoSomaSelecionado(): FormulaFuncaoBloco | null {
        if (this.activeTokenIndex === null) return null;
        const bloco = this.blocoEstruturalDoToken(this.activeTokenIndex);
        return bloco?.nome === '@soma' ? bloco : null;
    }

    public get quantidadeParametrosSoma(): number {
        const bloco = this.blocoSomaSelecionado;
        return bloco ? this.separadoresTopo(bloco).length + 1 : 0;
    }

    public adicionarParametroSoma(): void {
        const bloco = this.blocoSomaSelecionado;
        if (!bloco) return;
        this.tokens.splice(bloco.end, 0, {
            tipo: FormulaTokenTipoEnum.Separador,
            valor: ',',
            display: ',',
            funcaoGrupoIds: [...(this.tokens[bloco.start].funcaoGrupoIds ?? [])],
            funcaoEstruturalId: bloco.id,
        });
        this.cursorIndex = bloco.end + 1;
        this.emit();
        this.selecionarBlocoPorInicio(bloco.start);
    }

    public removerParametroSoma(): void {
        const bloco = this.blocoSomaSelecionado;
        if (!bloco || this.quantidadeParametrosSoma <= 1) return;
        const ultimoSeparador = this.separadoresTopo(bloco).at(-1)!;
        const possuiConteudo = ultimoSeparador + 1 < bloco.end;
        if (!possuiConteudo) {
            this.removerUltimoParametro(bloco.start, ultimoSeparador, bloco.end);
            return;
        }

        this.dialog.open(ConfirmModalComponent, {
            width: '460px',
            data: {
                title: 'Remover parâmetro',
                description: 'O último parâmetro possui conteúdo. Deseja removê-lo da função @soma?',
                buttonConfirm: 'Remover',
                buttonClose: 'Cancelar',
            },
        }).afterClosed().subscribe(resultado => {
            if (resultado?.confirm) this.removerUltimoParametro(bloco.start, ultimoSeparador, bloco.end);
        });
    }

    private removerUltimoParametro(blocoStart: number, separador: number, blocoEnd: number): void {
        this.tokens.splice(separador, blocoEnd - separador);
        this.cursorIndex = separador;
        this.emit();
        this.selecionarBlocoPorInicio(blocoStart);
    }

    private separadoresTopo(bloco: FormulaFuncaoBloco): number[] {
        return [...bloco.skeleton]
            .filter(index => this.tokens[index]?.tipo === FormulaTokenTipoEnum.Separador)
            .sort((a, b) => a - b);
    }

    private selecionarBlocoPorInicio(start: number): void {
        const bloco = this.funcaoBlocos.find(item => item.start === start);
        if (!bloco) return;
        this.activeTokenIndex = bloco.start;
        this.selectedSet = new Set(this.indicesEstruturais(bloco));
    }

    // ─────────────────────────── Edição inline da constante ───────────────────────────

    public get tokenNumeroSelecionado(): ExpressionToken | null {
        if (this.editing?.tipo === FormulaTokenTipoEnum.Constante) return this.editing;
        if (this.activeTokenIndex === null) return null;
        const token = this.tokens[this.activeTokenIndex];
        return token?.tipo === FormulaTokenTipoEnum.Constante ? token : null;
    }

    public get tipoConstanteSelecionada(): TipoConstanteNumericaEnum {
        if (this.editing === this.tokenNumeroSelecionado) return this.editingTipo;
        const conteudo = this.tokenNumeroSelecionado?.valor.replace(/#/g, '') ?? '';
        return conteudo.includes('.') ? TipoConstanteNumericaEnum.Decimal : TipoConstanteNumericaEnum.Inteiro;
    }

    public editarSelecionado(): void {
        const t = this.tokenNumeroSelecionado;
        if (t) this.startEdit(t);
    }

    private startEdit(token: ExpressionToken): void {
        const index = this.tokens.indexOf(token);
        if (index < 0) return;

        // A edição inline é um contexto exclusivo: a constante mantém as ações
        // próprias no rodapé, sem conservar seleção de função ou caret entre tokens.
        this.selectedSet = new Set();
        this.activeTokenIndex = index;
        this.cursorIndex = index + 1;
        this.editing = token;
        this.limiteConstanteNotificado = false;
        if (token.valor) {
            this.editingTipo = token.valor.replace(/#/g, '').includes('.')
                ? TipoConstanteNumericaEnum.Decimal
                : TipoConstanteNumericaEnum.Inteiro;
        }
        // ngx-currency armazena o valor NUMÉRICO no FormControl; ao reeditar um token
        // já preenchido, semeamos o número existente (e não uma string formatada, que
        // a máscara não consegue interpretar e acabaria zerando o campo).
        const numero = token.valor ? Number(token.valor.replace(/#/g, '')) : null;
        this.editControl.setValue(Number.isFinite(numero as number) ? numero : null, { emitEvent: false });
        setTimeout(() => this.constInputs?.first?.nativeElement?.focus());
    }

    public onConstanteKeyDown(event: KeyboardEvent): void {
        if (!/^\d$/.test(event.key)) {
            if (event.key === 'Backspace' || event.key === 'Delete') this.limiteConstanteNotificado = false;
            return;
        }

        const input = event.target as HTMLInputElement;
        if (this.quantidadeDigitosAposInsercao(input, event.key) <= this.limiteDigitosConstante) {
            this.limiteConstanteNotificado = false;
            return;
        }

        event.preventDefault();
        this.notificarLimiteConstante();
    }

    public onConstantePaste(event: ClipboardEvent): void {
        const input = event.target as HTMLInputElement;
        const texto = event.clipboardData?.getData('text') ?? '';
        if (this.quantidadeDigitosAposInsercao(input, texto) <= this.limiteDigitosConstante) {
            this.limiteConstanteNotificado = false;
            return;
        }

        event.preventDefault();
        this.notificarLimiteConstante();
    }

    private quantidadeDigitosAposInsercao(input: HTMLInputElement, textoInserido: string): number {
        const inicio = input.selectionStart ?? input.value.length;
        const fim = input.selectionEnd ?? inicio;
        const valorResultante = input.value.slice(0, inicio) + textoInserido + input.value.slice(fim);
        return valorResultante.replace(/\D/g, '').length;
    }

    private notificarLimiteConstante(): void {
        if (this.limiteConstanteNotificado) return;
        this.limiteConstanteNotificado = true;

        const mensagem = this.editingTipo === TipoConstanteNumericaEnum.Inteiro
            ? 'A constante numérica inteira aceita no máximo 9 dígitos (999.999.999).'
            : 'A constante numérica decimal aceita no máximo 9 dígitos inteiros e 2 casas decimais (999.999.999,99).';
        this.alertService.show('Atenção', mensagem, AlertType.warning);
    }

    /** Commit do valor da constante (Enter, blur ou clique fora). Vazio => 0. */
    public commitEdit(): void {
        if (!this.editing) return;
        const token = this.editing;
        const index = this.tokens.indexOf(token);
        const val = this.editControl.value;
        const numero = typeof val === 'number' && Number.isFinite(val) ? val : 0;
        const numeroLimitado = Math.min(numero, this.valorMaximoConstante(this.editingTipo));

        token.valor = this.editingTipo === TipoConstanteNumericaEnum.Inteiro
            ? `#${Math.trunc(numeroLimitado)}#`
            : `#${numeroLimitado.toFixed(2)}#`;
        token.display = this.formatarNumeroDisplay(token.valor);
        this.editing = null;
        if (index >= 0) {
            this.selectedSet = new Set([index]);
            this.activeTokenIndex = index;
            this.cursorIndex = index + 1;
        }
        this.emit();
    }

    public alterarTipoConstante(tipo: TipoConstanteNumericaEnum): void {
        const token = this.tokenNumeroSelecionado;
        if (!token || tipo === this.tipoConstanteSelecionada) return;
        const numero = Number(token.valor.replace(/#/g, ''));

        if (tipo === TipoConstanteNumericaEnum.Decimal) {
            this.aplicarTipoConstante(token, numero, tipo);
            return;
        }

        if (Number.isInteger(numero)) {
            this.aplicarTipoConstante(token, numero, tipo);
            return;
        }

        this.dialog.open(ConstanteNumericaConversaoDialogComponent, { width: '480px' })
            .afterClosed()
            .subscribe((conversao: TipoArredondamentoEnum | undefined) => {
                if (conversao === undefined) return;
                const valor = conversao === TipoArredondamentoEnum.Truncar
                    ? Math.trunc(numero)
                    : UtilService.arredondarAbnt(numero, 0);
                this.aplicarTipoConstante(token, valor, tipo);
            });
    }

    private aplicarTipoConstante(token: ExpressionToken, numero: number, tipo: TipoConstanteNumericaEnum): void {
        const numeroLimitado = Math.min(numero, this.valorMaximoConstante(tipo));
        token.valor = tipo === TipoConstanteNumericaEnum.Inteiro
            ? `#${Math.trunc(numeroLimitado)}#`
            : `#${numeroLimitado.toFixed(2)}#`;
        token.display = this.formatarNumeroDisplay(token.valor);
        this.editingTipo = tipo;
        this.emit();
    }

    private valorMaximoConstante(tipo: TipoConstanteNumericaEnum): number {
        return tipo === TipoConstanteNumericaEnum.Inteiro
            ? FormulaExpressaoEditorComponent.VALOR_MAXIMO_INTEIRO
            : FormulaExpressaoEditorComponent.VALOR_MAXIMO_DECIMAL;
    }

    private commitEditIfAny(): void {
        if (this.editing) this.commitEdit();
    }

    // ─────────────────────────── Render helpers ───────────────────────────

    public tokenClass(i: number): string {
        const token = this.tokens[i];
        const blocoFuncao = obterBlocoFuncaoEstrutural(this.funcaoBlocos, i);
        if (blocoFuncao) return classeBadgeFuncao(blocoFuncao.nome, blocoFuncao.nivel);

        if (token.tipo === FormulaTokenTipoEnum.Variavel) {
            return classeBadgeVariavel(this.obterVariavelToken(token));
        }
        const formulaToken: FormulaExpressaoToken = {
            tipo: token.tipo === FormulaTokenTipoEnum.Funcao ? FormulaTokenTipoEnum.Funcao
                : token.tipo === FormulaTokenTipoEnum.Parentese ? FormulaTokenTipoEnum.Parentese
                : token.tipo === FormulaTokenTipoEnum.Constante ? FormulaTokenTipoEnum.Constante
                : token.tipo === FormulaTokenTipoEnum.Separador ? FormulaTokenTipoEnum.Separador : FormulaTokenTipoEnum.Operador,
            valor: token.valor.trim(),
            display: token.display,
        };
        return classeBadgeTokenFormula(formulaToken);
    }

    public variavelNome(token: ExpressionToken): string {
        if (token.tipo !== FormulaTokenTipoEnum.Variavel) return token.display;
        const tag = token.valor.slice(1, -1);
        return this.variaveis.find(v => this.variavelChave(v) === tag)?.descricao ?? tag;
    }

    public variavelNomeVariavel(token: ExpressionToken): string {
        if (token.tipo !== FormulaTokenTipoEnum.Variavel) return '';
        const tag = token.valor.slice(1, -1);
        return this.variaveis.find(v => this.variavelChave(v) === tag)?.nome ?? tag;
    }

    public possuiFormulaAplicavel(token: ExpressionToken): boolean {
        return this.obterVariavelToken(token)?.possuiFormulaAplicavel === true;
    }

    public isParametro(token: ExpressionToken): boolean {
        return isVariavelParametro(this.obterVariavelToken(token));
    }

    public variavelChaveToken(token: ExpressionToken): string {
        if (token.tipo !== FormulaTokenTipoEnum.Variavel) return '';
        return token.valor.slice(1, -1);
    }

    public isTokenComErro(i: number): boolean {
        return this.mensagensErroToken(i).length > 0;
    }

    public mensagensErroToken(i: number): string {
        if (this.editing) return '';
        return (this.validacao.erros ?? [])
            .filter(erro => erro.tokenIndex === i)
            .map(erro => erro.mensagem)
            .join(' • ');
    }

    public get mensagemErroTokenAtivo(): string {
        return this.activeTokenIndex === null ? '' : this.mensagensErroToken(this.activeTokenIndex);
    }

    public exibirTooltipErro(i: number, tooltip: MatTooltip): void {
        if (!this.isTokenComErro(i)) return;
        tooltip.show(0);
        setTimeout(() => tooltip.hide(0), 4000);
    }

    public get validacao(): FormulaExpressaoValidacao {
        if (this.editing) return { valida: true };
        return validarExpressaoFormula(
            this.serialize(),
            this.variaveis.map(v => this.variavelChave(v)),
            this.variaveis
                .filter(v => v.tipoDadoVariavel === TipoDadoVariavelEnum.Booleano)
                .map(v => this.variavelChave(v)),
        );
    }

    public get temConteudo(): boolean {
        return this.tokens.length > 0;
    }

    private formatarNumeroDisplay(valorUS: string): string {
        const conteudo = valorUS.replace(/#/g, '');
        const n = Number(conteudo);
        if (!Number.isFinite(n)) return valorUS;
        const casasDecimais = conteudo.includes('.') ? Math.max(2, conteudo.split('.')[1].length) : 0;
        return n.toLocaleString('pt-BR', {
            minimumFractionDigits: casasDecimais,
            maximumFractionDigits: casasDecimais,
        });
    }

    /** Consolida operadores compostos quando suas partes ficam adjacentes por teclado ou arraste. */
    private normalizarOperadoresCompostos(): void {
        for (let i = 0; i < this.tokens.length - 1; i++) {
            const atual = this.tokens[i];
            const proximo = this.tokens[i + 1];
            if (atual.tipo !== FormulaTokenTipoEnum.Operador || proximo.tipo !== FormulaTokenTipoEnum.Operador) continue;
            if ((atual.funcaoGrupoIds ?? []).join('|') !== (proximo.funcaoGrupoIds ?? []).join('|')) continue;

            const partes = [atual.valor.trim(), proximo.valor.trim()];
            let composto: string | null = null;
            if (partes.includes('=') && partes.includes('>')) composto = '>=';
            else if (partes.includes('=') && partes.includes('<')) composto = '<=';
            else if (partes.includes('<') && partes.includes('>')) composto = '<>';
            if (!composto) continue;

            this.tokens.splice(i, 2, {
                tipo: FormulaTokenTipoEnum.Operador,
                valor: ` ${composto} `,
                display: composto,
                funcaoGrupoIds: [...(atual.funcaoGrupoIds ?? [])],
            });
            this.activeTokenIndex = i;
            this.cursorIndex = Math.max(0, this.cursorIndex - 1);
        }
    }

    private variavelChave(variavel: VariavelStruct): string {
        return variavel.chave || variavel.tagXml || variavel.nome;
    }

    private obterVariavelToken(token: ExpressionToken): VariavelStruct | undefined {
        if (token.tipo !== FormulaTokenTipoEnum.Variavel) return undefined;
        const chave = token.valor.slice(1, -1);
        return this.variaveis.find(variavel => this.variavelChave(variavel) === chave);
    }
}
